import db, { getLastSourceCode, setLastSourceCode } from "../db/database";
import {
  fetchFromBusinessCentral,
  fetchPagedFromBusinessCentral,
} from "../utils/businessCentralUtils";
import { FILAS_DE_SOLAPE, marcaAGuardarConSolape } from "./margenDeSolape";
import { isOnline } from "./syncService";
import {
  anotarAvanceDeLectura,
  anotarAviso,
  anotarError,
  anotarLectura,
  anotarSinNovedades,
} from "./syncLog";

// Clave con la que se guarda el contador incremental en syncMetadata.
const ADDRESS_SYNC_ENTITY = "clientAddresses";

// Nombre con el que esta entidad aparece en las líneas de consola. No es el de
// la tabla: "clientAddresses" es el nombre interno del checkpoint.
const ENTIDAD = "direcciones";

// Las direcciones de envío de BC se identifican por CustomerNo + Code, no por
// Code solo: el Code es único POR CLIENTE. En Ecuador hay 16.604 clientes cuya
// dirección tiene Code "01" -- el que BC asigna al crear un cliente. Indexar
// sólo por Code hacía que cada dirección pisara a la anterior.
const addressKey = (address) => `${address.CustomerNo}|${address.Code}`;

const mapAddressFromBC = (serverAddress) => ({
  CustomerNo: serverAddress.CustomerNo,
  Code: serverAddress.Code,
  Name: serverAddress.Name,
  Ciudad: serverAddress.Ciudad,
  Provincia: serverAddress.Provincia,
  Email: serverAddress.Email || "",
  Teléfono: serverAddress.Teléfono || "",
  Source_Counter: serverAddress.Source_Counter ?? null,
  lastSynced: new Date(),
});

/**
 * Mayor Source_Counter del lote, o -1 si BC no mandó el campo en ninguno.
 *
 * Devolver -1 es lo que mantiene la sincronización en modo completo mientras BC
 * no exponga el contador: nunca se guarda un contador que no existe.
 *
 * @param {Array} addresses - Direcciones tal como las devuelve BC.
 * @returns {number} - Máximo Source_Counter, o -1 si no hay ninguno válido.
 */
export const getMaxSourceCounter = (addresses = []) =>
  addresses.reduce((max, address) => {
    const value = Number(address?.Source_Counter);
    return Number.isFinite(value) && value > max ? value : max;
  }, -1);

/**
 * Guarda en la base local las direcciones que vinieron de BC, respetando la
 * clave real (CustomerNo + Code): actualiza las que ya existen y agrega las
 * nuevas. Se escribe con bulkPut en una sola transacción -- antes eran ~20.000
 * update/add secuenciales, uno por dirección.
 *
 * @param {Array} serverAddresses - Direcciones tal como las devuelve BC.
 * @returns {Promise<number>} - Cantidad de direcciones guardadas.
 */
const saveAddresses = async (serverAddresses) => {
  if (!serverAddresses.length) return 0;

  // En el refresco de un cliente se leen sólo sus direcciones; en el pull
  // completo conviene una sola lectura de la tabla, porque un anyOf con miles
  // de claves es más caro que traerla entera.
  const customerNos = [...new Set(serverAddresses.map((a) => a.CustomerNo).filter(Boolean))];
  const localAddresses =
    customerNos.length > 500
      ? await db.clientAddresses.toArray()
      : await db.clientAddresses.where("CustomerNo").anyOf(customerNos).toArray();

  const localIdsByKey = new Map(localAddresses.map((a) => [addressKey(a), a.id]));

  const records = serverAddresses.map((serverAddress) => {
    const mapped = mapAddressFromBC(serverAddress);
    const existingId = localIdsByKey.get(addressKey(serverAddress));
    // bulkPut respeta el id cuando viene: así actualiza en vez de duplicar.
    return existingId ? { ...mapped, id: existingId } : mapped;
  });

  await db.clientAddresses.bulkPut(records);
  return records.length;
};

/**
 * Trae de BC las direcciones de un cliente puntual y las guarda localmente.
 *
 * Se usa al abrir las direcciones de un cliente: sin esto, un cliente recién
 * creado no muestra su dirección de envío hasta la próxima sincronización
 * completa, aunque BC ya se la haya creado.
 *
 * @param {string} customerNo - No del cliente en BC.
 * @returns {Promise<boolean>} - true si se pudo refrescar contra BC.
 */
export const syncClientAddressesByCustomer = async (customerNo) => {
  if (!customerNo || !isOnline()) {
    return false;
  }

  try {
    const serverAddresses = await fetchFromBusinessCentral(
      "direcciones",
      `CustomerNo eq '${customerNo}'`
    );

    if (!Array.isArray(serverAddresses)) return false;

    // Si una dirección se borró en BC, la local tampoco debe quedar.
    const locales = await db.clientAddresses.where("CustomerNo").equals(customerNo).toArray();
    const vigentes = new Set(serverAddresses.map(addressKey));
    const obsoletos = locales.filter((a) => !vigentes.has(addressKey(a))).map((a) => a.id);

    if (obsoletos.length) await db.clientAddresses.bulkDelete(obsoletos);

    await saveAddresses(serverAddresses);
    return true;
  } catch (error) {
    anotarError(ENTIDAD, `falló la lectura del cliente ${customerNo}`, error);
    return false;
  }
};

/**
 * Sincroniza las direcciones de clientes desde Business Central.
 *
 * Es incremental cuando BC expone `Source_Counter` en la entidad
 * `direcciones`, igual que ya hacen Clientes y Colegios: se pide sólo lo que
 * cambió desde el último contador guardado, en vez de bajar las `20.000
 * direcciones completas en cada sincronización.
 *
 * **Se activa sola.** Mientras BC no mande contadores reales, no se guarda
 * checkpoint y la descarga sigue siendo completa. En cuanto BC exponga el campo
 * Y la inicialización reparta contadores mayores a cero, la primera corrida
 * completa detecta el máximo, lo guarda, y a partir de la siguiente ya pide
 * sólo lo nuevo. No hace falta tocar este archivo ni desplegar de nuevo.
 *
 * **Limitación del modo incremental** (la misma que tienen Clientes y
 * Colegios): una dirección que se modifique o se borre en BC sin que suba su
 * contador no llega por esta vía. Para eso está
 * `syncClientAddressesByCustomer`, que refresca contra BC las direcciones del
 * cliente que se está mirando y borra las que ya no existan.
 */
export const syncClientAddresses = async () => {
  if (!isOnline()) {
    return false;
  }

  try {
    // Sin checkpoint mientras BC no exponga el contador, o si nunca corrió.
    //
    // El checkpoint se considera válido sólo si es MAYOR a cero, igual que en
    // clientSyncService y colegioSyncService. Cero no sirve como checkpoint y
    // además es peligroso: si BC publica el campo pero todavía no corrió la
    // inicialización que reparte los contadores, todos los registros llegan en
    // cero; guardarlo como checkpoint dejaría al TPV pidiendo "gt 0" para
    // siempre, y cualquier registro que se quede en cero no volvería a llegar
    // nunca. No es hipotético: hoy Colegios tiene 1.663 registros en cero.
    // Con esta regla, mientras no haya contadores reales se sigue bajando todo.
    const lastSourceCode = await getLastSourceCode(ADDRESS_SYNC_ENTITY);
    const incremental = lastSourceCode > 0;

    // El máximo contador visto, para avanzar el checkpoint al terminar. Se
    // acumula bloque a bloque porque nunca se tiene el lote entero en memoria.
    let maxSourceCounter = -1;

    // Y las marcas más altas del lote, para guardar la K-ésima en vez de la
    // mayor --el colchón de `margenDeSolape.js`--. Como el lote entero no cabe,
    // se conserva sólo la cabeza de la lista: no hace falta más para saber cuál
    // es la K-ésima.
    let contadoresDelLote = [];
    const recordarContadores = (page) => {
      for (const fila of page) {
        const valor = Number(fila?.Source_Counter);
        if (Number.isFinite(valor)) contadoresDelLote.push(valor);
      }
      if (contadoresDelLote.length > FILAS_DE_SOLAPE) {
        contadoresDelLote.sort((a, b) => b - a);
        contadoresDelLote = contadoresDelLote.slice(0, FILAS_DE_SOLAPE);
      }
    };

    // Se pagina por Source_Counter y no por el par (CustomerNo, Code): la clave
    // es única por dirección --19.950 valores distintos para 19.950
    // direcciones-- y hace la lectura estable si algo cambia entre bloques, que
    // el desplazamiento no garantizaba.
    const traerPorBloques = (filter) =>
      fetchPagedFromBusinessCentral("direcciones", {
        filter,
        keyField: "Source_Counter",
        onPage: async (page, procesados) => {
          await saveAddresses(page);
          maxSourceCounter = Math.max(maxSourceCounter, getMaxSourceCounter(page));
          recordarContadores(page);
          anotarAvanceDeLectura(ENTIDAD, procesados);
        },
      });

    let guardadas;

    try {
      guardadas = await traerPorBloques(incremental ? `Source_Counter gt ${lastSourceCode}` : "");
    } catch (error) {
      // Si el filtro deja de ser válido (BC quitó el campo, o le cambió el
      // nombre), se vuelve a la descarga completa y se reinicia el contador
      // para no quedar atascado sin recibir nada nunca más.
      if (!incremental) throw error;

      anotarAviso(
        ENTIDAD,
        `el filtro incremental falló, se vuelve a la descarga completa — ${error?.message || error}`
      );
      await setLastSourceCode(-1, ADDRESS_SYNC_ENTITY);
      maxSourceCounter = -1;
      contadoresDelLote = [];
      guardadas = await traerPorBloques("");
    }

    if (guardadas === 0) {
      anotarSinNovedades(ENTIDAD, incremental);
      return true;
    }

    // Sólo se guarda un checkpoint que sirva: ver el comentario de arriba.
    //
    // Y con colchón: se guarda la marca número `FILAS_DE_SOLAPE` del lote y no
    // la mayor, para que la lectura siguiente vuelva a pasar por esas filas.
    // Ver `margenDeSolape.js`. Releer una dirección la reescribe igual que
    // estaba, así que no cuesta nada.
    const marcaAGuardar = marcaAGuardarConSolape(contadoresDelLote, lastSourceCode);
    if (marcaAGuardar > 0) {
      await setLastSourceCode(marcaAGuardar, ADDRESS_SYNC_ENTITY);
    }

    anotarLectura(ENTIDAD, {
      guardados: guardadas,
      incremental,
      // La marca es el checkpoint que queda guardado. Antes la escribía la capa
      // de la base de datos, en su propia línea y con el nombre interno de la
      // entidad ("clientAddresses"). Es el primer dato que hay que mirar cuando
      // un catálogo deja de traer cosas.
      marca: maxSourceCounter > 0 ? maxSourceCounter : undefined,
    });
    return true;
  } catch (error) {
    anotarError(ENTIDAD, "falló la lectura", error);
    return false;
  }
};
